import { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { theme } from "src/theme/mui-theme";
import { ts } from "src/utils/translate";
import { FloorFormData, RoomCreationMethod } from "src/pages/PlannerPage/FloorWizard/useUpsertFloor";
import { ManualRoomDrawingIcon } from "src/pages/PlannerPage/FloorWizard/icons/ManualRoomDrawingIcon";
import { AutomaticRoomRecognitionIcon } from "src/pages/PlannerPage/FloorWizard/icons/AutomaticRoomRecognitionIcon";

interface RoomCreationMethodStepProps {
    formData: FloorFormData;
    setFormData: (data: FloorFormData) => void;
}

interface MethodOption {
    id: RoomCreationMethod;
    label: string;
    description: string;
    icon: ReactNode;
    beta?: boolean;
}

export const FloorWizardRoomCreationMethodStep = ({ formData, setFormData }: RoomCreationMethodStepProps) => {
    const methods: MethodOption[] = [
        {
            id: RoomCreationMethod.MANUAL,
            label: ts('manual_room_drawing', 'Manual room drawing'),
            description: ts('manual_room_drawing_description', 'Draw all your rooms manually for full control'),
            icon: <ManualRoomDrawingIcon />,
        },
        {
            id: RoomCreationMethod.AUTOMATIC,
            label: ts('automatic_room_recognition', 'Automatic room recognition'),
            description: ts('automatic_room_recognition_description', 'Rooms detected instantly based on your layout'),
            icon: <AutomaticRoomRecognitionIcon />,
            beta: true,
        },
    ];

    return (
        <Stack direction="row" spacing={3} sx={{ my: 2 }}>
            {methods.map(({ id, label, description, icon, beta }) => {
                const isSelected = formData.roomCreationMethod === id;
                return (
                    <Stack
                        key={id}
                        alignItems="center"
                        onClick={() => setFormData({ ...formData, roomCreationMethod: id })}
                        sx={{
                            position: "relative",
                            flex: 1,
                            cursor: "pointer",
                            borderRadius: 2,
                            border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.grey[300]}`,
                            backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.05) : "transparent",
                            p: 2,
                        }}
                    >
                        {beta && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                }}
                            >
                                {ts('beta', 'BETA')}
                            </Box>
                        )}

                        <Box sx={{ width: 60, height: 60, color: theme.palette.primary.main }}>
                            {icon}
                        </Box>

                        <Typography
                            variant="subtitle2"
                            color={theme.palette.primary.main}
                            textAlign="center"
                            sx={{ mt: 1.5 }}
                        >
                            {label}
                        </Typography>

                        <Typography
                            variant="caption"
                            color={theme.palette.text.secondary}
                            textAlign="center"
                            sx={{ mt: 0.5 }}
                        >
                            {description}
                        </Typography>
                    </Stack>
                );
            })}
        </Stack>
    );
};